"use client";

/**
 * useCurrentSource - Detects the active market source from URL prefix
 *
 * Priority:
 * 1. URL pathname prefix: /polymarket/... or /kalshi/...
 * 2. URL search params: ?source=kalshi or ?_source=kalshi (internal rewrite param)
 * 3. localStorage (persists across refresh, tabs, and sessions)
 * 4. Default: "polymarket"
 */

import { useSearchParams, usePathname } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { sourceFromPathname, type Source } from "@/lib/source-url";

export type MarketSource = "polymarket" | "kalshi" | "predict" | "hyperliquid";

const STORAGE_KEY = "frenflow_source";
const VALID_SOURCES: MarketSource[] = ["polymarket", "kalshi", "predict", "hyperliquid"];

function getSavedSource(): MarketSource | null {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && VALID_SOURCES.includes(saved as MarketSource)) {
      return saved as MarketSource;
    }
  } catch { /* ignore */ }
  return null;
}

export function saveSource(source: MarketSource) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, source);
  } catch { /* ignore */ }
}

export function useCurrentSource(): MarketSource {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  // Re-render trigger for source changes on non-navigating pages (e.g. /settings)
  const [, setTick] = useState(0);

  // Listen for source-change events from SourceSelector
  useEffect(() => {
    const handler = () => setTick(t => t + 1);
    window.addEventListener('frenflow:source-change', handler);
    return () => window.removeEventListener('frenflow:source-change', handler);
  }, []);

  // 1. URL prefix (primary — clean URLs)
  const prefixSource = sourceFromPathname(pathname);

  // 2. URL search params (legacy ?source= or internal ?_source= from middleware rewrite)
  const urlSource = searchParams.get("source") || searchParams.get("_source");
  const normalizedSource = urlSource;

  // 3. Pathname-based detection (/event/kalshi/..., /traders/predict, etc.)
  // Note: /traders/kalshi was removed (no Kalshi traders surface), so only
  // /event/kalshi/ implies a Kalshi source now.
  const pathSource = pathname.startsWith("/event/kalshi/")
    ? "kalshi"
    : pathname.startsWith("/event/predict/") || pathname.startsWith("/traders/predict")
    ? "predict"
    : null;

  const detected = prefixSource || normalizedSource || pathSource;

  // Routes that imply a specific source by their pathname (no prefix = Polymarket)
  const isSourceableRoute = /^\/(markets|market|event)(\/|$)/.test(pathname);

  let source: MarketSource = "polymarket";

  if (detected && VALID_SOURCES.includes(detected as MarketSource)) {
    source = detected as MarketSource;
  } else if (isSourceableRoute) {
    // Unprefixed /markets, /market/*, /event/* are always Polymarket
    source = "polymarket";
  } else {
    // Non-market pages (settings, traders, etc.) use localStorage
    source = getSavedSource() || "polymarket";
  }

  // Persist whenever source changes
  useEffect(() => {
    saveSource(source);
  }, [source]);

  return source;
}
